"use client";

import { useEffect, useRef, useState } from "react";
import type { Map as LeafletMap } from "leaflet";
import RideRoutePreview from "@/app/components/RideRoutePreview";
import {
  GOODS_TRANSPORT_TYPES,
  GOODS_TRANSPORT_VEHICLES,
} from "@/lib/goodsTransportOptions";

type Point = {
  latitude: number;
  longitude: number;
  label: string;
};

type BookingStatus = {
  id: number;
  status: string;
  vehicleType: string;
};

type Props = {
  map: LeafletMap | null;
  loggedIn: boolean;
  onClose: () => void;
};

export default function HomepageGoodsTransportPanel({
  map,
  loggedIn,
  onClose,
}: Props) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [pickup, setPickup] = useState<Point | null>(null);
  const [destination, setDestination] = useState<Point | null>(null);
  const [goodsType, setGoodsType] = useState<string>("");
  const [goodsDetails, setGoodsDetails] = useState("");
  const [vehicleType, setVehicleType] = useState<string>("");
  const [booking, setBooking] = useState<BookingStatus | null>(null);
  const [driverLocation, setDriverLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [pending, setPending] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState("");
  const submittingRef = useRef(false);

  // Reuse the homepage map without modifying passenger Ride Now state.
  useEffect(() => {
    if (!map || booking || (step !== 1 && step !== 2)) return;

    function handleMapClick(event: import("leaflet").LeafletMouseEvent) {
      const point = {
        latitude: event.latlng.lat,
        longitude: event.latlng.lng,
        label: `${event.latlng.lat.toFixed(5)}, ${event.latlng.lng.toFixed(5)}`,
      };

      if (step === 1) {
        setPickup(point);
      } else {
        setDestination(point);
      }

      setError("");
    }

    map.on("click", handleMapClick);
    return () => {
      map.off("click", handleMapClick);
    };
  }, [map, step, booking]);

  // Show pickup, destination and assigned driver on the same map.
  useEffect(() => {
    if (!map) return;

    let cancelled = false;
    let layer: import("leaflet").LayerGroup | null = null;

    void import("leaflet").then((L) => {
      if (cancelled) return;

      layer = L.layerGroup().addTo(map);

      if (pickup) {
        L.circleMarker([pickup.latitude, pickup.longitude], {
          radius: 9, color: "#047857", fillOpacity: 0.9,
        }).bindTooltip("Goods pickup").addTo(layer);
      }

      if (destination) {
        L.circleMarker([destination.latitude, destination.longitude], {
          radius: 9, color: "#dc2626", fillOpacity: 0.9,
        }).bindTooltip("Goods destination").addTo(layer);
      }

      if (driverLocation && booking?.status === "Accepted") {
        L.circleMarker(
          [driverLocation.latitude, driverLocation.longitude],
          { radius: 11, color: "#2563eb", fillOpacity: 1 }
        ).bindTooltip("Assigned goods driver").addTo(layer);
      }
    });

    return () => {
      cancelled = true;
      layer?.remove();
    };
  }, [map, pickup, destination, driverLocation, booking?.status]);

  // Poll only this customer's authenticated booking.
  useEffect(() => {
    if (!booking) return;

    let cancelled = false;
    let controller: AbortController | null = null;

    async function refresh() {
      controller?.abort();
      controller = new AbortController();

      try {
        const response = await fetch(
          `/api/goods-transport/${booking!.id}`,
          { cache: "no-store", signal: controller.signal }
        );

        if (!response.ok) return;

        const data = await response.json();
        if (cancelled || !data.booking) return;

        setBooking({
          id: data.booking.id,
          status: data.booking.status,
          vehicleType: data.booking.vehicleType,
        });

        setDriverLocation(
          data.booking.status === "Accepted"
            ? data.driverLocation ?? null
            : null
        );
      } catch {
        // Temporary network problems should not erase booking state.
      }
    }

    void refresh();
    const timer = window.setInterval(() => void refresh(), 5000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      controller?.abort();
    };
  }, [booking?.id]);

  async function confirmBooking() {
    if (submittingRef.current || booking) return;

    if (!loggedIn) {
      setError("Please log in before confirming Goods Transport.");
      return;
    }

    if (!pickup || !destination || !goodsType || !vehicleType) {
      setError("Complete pickup, destination, goods and vehicle selection.");
      return;
    }

    submittingRef.current = true;
    setPending(true);
    setError("");

    try {
      const response = await fetch("/api/goods-transport", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pickupLocation: pickup.label,
          pickupLatitude: pickup.latitude,
          pickupLongitude: pickup.longitude,
          dropLocation: destination.label,
          dropLatitude: destination.latitude,
          dropLongitude: destination.longitude,
          goodsType,
          goodsDetails,
          vehicleType,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Booking could not be created.");
      }

      setBooking(data.booking);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Booking request failed."
      );
    } finally {
      setPending(false);
      submittingRef.current = false;
    }
  }

  async function cancelBooking() {
    if (!booking || booking.status !== "Open" || cancelling) return;

    if (!window.confirm("Cancel this Goods Transport booking?")) return;

    setCancelling(true);
    setError("");

    try {
      const response = await fetch(
        `/api/goods-transport/${booking.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "cancel" }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Booking could not be cancelled."
        );
      }

      setBooking((current) =>
        current?.id === booking.id
          ? { ...current, status: "Cancelled" }
          : current
      );
      setDriverLocation(null);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Unable to cancel booking."
      );
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="col-span-2 w-full min-w-0 max-h-[65dvh] space-y-3 overflow-y-auto rounded-xl bg-white/90 p-3 sm:col-span-4">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-slate-900">Goods Transport 🚚</h2>
        <button type="button" onClick={onClose}
          className="text-sm font-semibold text-indigo-700">
          Close
        </button>
      </div>

      {booking ? (
        <div className="space-y-2">
          <p className="font-semibold">
            Booking #{booking.id} — {booking.status}
          </p>
          <p className="text-sm text-slate-600">
            {booking.status === "Open"
              ? "Waiting for a matching Goods Transport driver..."
              : booking.status === "Accepted"
                ? "Driver accepted! Live position appears on this map when GPS is available."
                : `Booking status: ${booking.status}`}
          </p>
          <p className="text-xs text-slate-500">
            Selected vehicle: {booking.vehicleType}
          </p>
          {booking.status === "Open" && (
            <button
              type="button"
              disabled={cancelling}
              onClick={() => void cancelBooking()}
              className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 disabled:opacity-50"
            >
              {cancelling ? "Cancelling..." : "Cancel Booking"}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {step === 1 && (
            <div className="space-y-2">
              <p className="font-semibold">1/4 · Pickup location</p>
              <p className="text-sm">Tap the pickup point on the map.</p>
              <p className="text-sm">{pickup?.label ?? "Pickup not selected"}</p>
              <button type="button" disabled={!pickup}
                onClick={() => setStep(2)}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-white disabled:opacity-50">
                Next: Destination
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-2">
              <p className="font-semibold">2/4 · Destination</p>
              <p className="text-sm">Tap the destination on the map.</p>
              <p className="text-sm">{destination?.label ?? "Destination not selected"}</p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setStep(1)}
                  className="rounded-lg border px-4 py-2">Back</button>
                <button type="button" disabled={!destination}
                  onClick={() => setStep(3)}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-white disabled:opacity-50">
                  Next: Goods
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold" htmlFor="goods-type">
                3/4 · Kya Samaan Hai?
              </label>
              <select id="goods-type" value={goodsType}
                onChange={(event) => setGoodsType(event.target.value)}
                className="w-full rounded-lg border p-2">
                <option value="">Select goods</option>
                {GOODS_TRANSPORT_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <textarea value={goodsDetails} maxLength={500}
                onChange={(event) => setGoodsDetails(event.target.value)}
                placeholder="Optional goods details"
                className="w-full rounded-lg border p-2" rows={2} />
              <div className="flex gap-2">
                <button type="button" onClick={() => setStep(2)}
                  className="rounded-lg border px-4 py-2">Back</button>
                <button type="button" disabled={!goodsType}
                  onClick={() => setStep(4)}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-white disabled:opacity-50">
                  Next: Vehicle
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold" htmlFor="goods-vehicle">
                4/4 · Select Goods Vehicle
              </label>
              <select id="goods-vehicle" value={vehicleType}
                onChange={(event) => setVehicleType(event.target.value)}
                className="w-full rounded-lg border p-2">
                <option value="">Select vehicle</option>
                {GOODS_TRANSPORT_VEHICLES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <p className="text-xs text-slate-600">
                Pickup: {pickup?.label} → Destination: {destination?.label}
              </p>
              <div className="flex gap-2">
                <button type="button" disabled={pending}
                  onClick={() => setStep(3)}
                  className="rounded-lg border px-4 py-2">Back</button>
                <button type="button" disabled={!vehicleType || pending}
                  onClick={() => void confirmBooking()}
                  className="rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white disabled:opacity-50">
                  {pending ? "Confirming..." : "Confirm Booking"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {pickup && destination && (
        <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-3">
          <p className="mb-1 text-xs font-bold text-indigo-900">
            Goods Transport · Road Route
          </p>
          <RideRoutePreview
            pickup={pickup}
            destination={destination}
            map={map}
          />
        </div>
      )}

      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
